// Pro's price per bundle in each currency Checkout bills, and the price of the pack of 3 Pro
// videos (paid once), written out so the pricing page and the country pages can show every
// country's price to a reader (or a search engine) that is not in that country. The live price
// for the visitor still comes from the API (hooks/useLocalPricing). A backend test keeps these
// equal to backend/api/api/services/local_pricing.py PRICES and services/packs.py PACK_PRICES.
import { currencySymbol, formatExact, formatMoney } from '../utils/money.js';

export const LOCAL_PRICES = [
  { currency: 'mxn', symbol: 'MX$', monthly: 199, yearly: 1990, pack: 69 },
  { currency: 'cop', symbol: 'COP$', monthly: 44900, yearly: 449000, pack: 13900 },
  { currency: 'pen', symbol: 'S/', monthly: 39, yearly: 390, pack: 14 },
  { currency: 'clp', symbol: 'CLP$', monthly: 10990, yearly: 109900, pack: 3390 },
  { currency: 'brl', symbol: 'R$', monthly: 59, yearly: 590, pack: 19.90 },
  { currency: 'try', symbol: '₺', monthly: 949, yearly: 9490, pack: 299 },
  { currency: 'eur', symbol: '€', monthly: 15, yearly: 150, pack: 4.99 },
  { currency: 'usd', symbol: 'US$', monthly: 19, yearly: 190, pack: 5.99 },
];

// "MX$1,990", "COP$44.900", "₺9.490": the API's format_amount (Colombia, Chile and Turkey group with dots)
export function formatLocalPrice(currency, amount) {
  const entry = LOCAL_PRICES.find((p) => p.currency === currency);
  const grouped = String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, ['cop', 'clp', 'try'].includes(currency) ? '.' : ',');
  return `${entry ? entry.symbol : `${currency.toUpperCase()} `}${grouped}`;
}

export const localPrice = (currency, period = 'monthly') => {
  const entry = LOCAL_PRICES.find((p) => p.currency === currency);
  return formatLocalPrice(currency, entry[period]);
};

// French and German write the amount first, a decimal comma, the symbol after a no-break space ("5,99 US$",
// "4,99 €", "19 US$"; i18n/FRENCH_STYLE.md, GERMAN_STYLE.md), thousands with a narrow space or a dot. So does Spain
// ('es-ES', the Spain country page only: "4,99 €", "15 €"); the rest of the Spanish site writes the API's "€4.99". And
// Portugal ('pt-PT', the Portugal country page only: "4,99 €", "15 €", thousands with a space); the rest of the
// Portuguese site is Brazil's
const AMOUNT_FIRST = { fr: '\u202f', de: '.', 'es-ES': '.', 'pt-PT': '\u00a0' };

/** A label in the API's style (format_amount: "R$19.90", "€4.99", "COP$13.900") written the way the page's language
 *  writes it: reais on a Portuguese page take the decimal comma and the thousands dot ("R$19,90", "R$2.950"); a French
 *  or German page writes "5,99 US$", "4,99 €", "19 US$"; everything else stays as the API writes it. A trailing ".dd" is
 *  cents in every currency (thousands come in threes). */
function restyle(label, currency, locale) {
  const symbol = currencySymbol(currency);
  const [, whole, cents] = (label.startsWith(symbol) && label.slice(symbol.length).match(/^(\d[\d.,]*?)(?:\.(\d{2}))?$/)) || [];
  if (whole == null) return label;
  const digits = whole.replace(/[.,]/g, '');
  if (locale in AMOUNT_FIRST) {
    return `${digits.replace(/\B(?=(\d{3})+(?!\d))/g, AMOUNT_FIRST[locale])}${cents ? `,${cents}` : ''}\u00a0${symbol.trim()}`;
  }
  if (locale === 'pt' && currency === 'brl') return `${symbol}${digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${cents ? `,${cents}` : ''}`;
  return label;
}

/** An amount as the page's language writes it (restyle): "R$19,90" on a Portuguese page, "5,99 US$" on a French or
 *  German one, else as the API's label writes it ("MX$69", "R$19.90", "€4.99", "COP$13.900"). formatMoney keeps the
 *  cents that formatLocalPrice would round away. */
export const formatForPage = (currency, amount, locale) => restyle(formatMoney(currency, amount), currency, locale);

/** Pro's month or year (`period`) in `currency`, as a page in `locale` writes it ("15 €" for 'es-ES' and 'pt-PT', else as
 *  localPrice). */
export const periodPrice = (currency, period = 'monthly', locale) => {
  const entry = LOCAL_PRICES.find((p) => p.currency === currency);
  return formatForPage(currency, entry[period], locale);
};

/** An amount to the cent (formatExact: "MX$79.50", a half first month) as the page's language writes it. */
export const formatExactForPage = (currency, amount, locale) => restyle(formatExact(currency, amount), currency, locale);

/** The pack of 3 Pro videos in `currency`, for a page in `locale` ("R$19,90" in Portuguese, else "R$19.90"). */
export const packPrice = (currency, locale) => {
  const entry = LOCAL_PRICES.find((p) => p.currency === currency);
  return formatForPage(currency, entry.pack, locale);
};

/** An offer from GET /subscription/pricing (the pack, the clip pack, Pro's month or year) written for a page in `locale`:
 *  the API's label unless the page's language writes the amount another way. Dollars when there is none. The app's
 *  offers and the pricing page reach it through hooks/useLocalPricing (`label`), so one screen never shows two styles. */
export function offerLabel(offer, locale) {
  if (!offer?.currency) return offer?.label || packPrice('usd', locale);
  // A country's own dollar price (Egypt: the pounds it comes to ride beside it, local_amount) stays as the API writes it
  if (offer.local_amount != null && offer.label) return offer.label;
  const own = formatForPage(offer.currency, offer.amount, locale);
  return own !== formatMoney(offer.currency, offer.amount) ? own : (offer.label || own);
}

/** Pro's month for this visitor (hooks/useLocalPricing: `monthly` beside the pricing's `currency`), written for a page
 *  in `locale`; dollars when there is no answer yet. The pack and Pro go through the same writer, so one sentence
 *  never mixes "5,99 US$" with "US$19". */
export function proLabel(pricing, locale, period = 'monthly') {
  const offer = pricing?.[period];
  if (!offer || offer.amount == null) return formatForPage('usd', LOCAL_PRICES.find((p) => p.currency === 'usd')[period], locale);
  return offerLabel({ ...offer, currency: offer.currency || pricing.currency || 'usd' }, locale);
}
